import React from 'react';
import { Zap, AlertCircle } from 'lucide-react';
import type { UploadedImage, ResizeOptions, WatermarkOptions, RenameOptions } from '../types';
import UploadZone from './UploadZone';
import ResizeControls from './ResizeControls';
import WatermarkControls from './WatermarkControls';
import RenameControls from './RenameControls';
import ImagePreview from './ImagePreview';

interface BatchEditorProps {
  images: UploadedImage[];
  setImages: React.Dispatch<React.SetStateAction<UploadedImage[]>>;
  resizeOptions: ResizeOptions;
  setResizeOptions: (options: ResizeOptions) => void;
  watermarkOptions: WatermarkOptions;
  setWatermarkOptions: (options: WatermarkOptions) => void;
  renameOptions: RenameOptions;
  setRenameOptions: (options: RenameOptions) => void;
  onProcess: () => void;
  error: string | null;
  setError: (error: string | null) => void;
  userId?: string | null;
}

const BatchEditor: React.FC<BatchEditorProps> = ({
  images,
  setImages,
  resizeOptions,
  setResizeOptions,
  watermarkOptions,
  setWatermarkOptions,
  renameOptions,
  setRenameOptions,
  onProcess,
  error,
  setError,
  userId,
}) => {
  return (
    <div className="flex-1 container mx-auto px-4 py-6 sm:py-8 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-100 mb-1">Batch Image Editor</h1>
        <p className="text-sm text-slate-400">Upload images, configure settings, then process and download as ZIP.</p>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-900/20 border border-red-900/50 rounded-lg text-red-400 text-sm mb-5 animate-shake">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">&times;</button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column: Upload + Controls */}
        <div className="lg:col-span-7 space-y-5">
          <UploadZone images={images} setImages={setImages} setError={setError} />

          {images.length > 0 && (
            <>
              <ResizeControls options={resizeOptions} onChange={setResizeOptions} />
              <WatermarkControls options={watermarkOptions} onChange={setWatermarkOptions} userId={userId} />
              <RenameControls options={renameOptions} onChange={setRenameOptions} imageCount={images.length} />

              <button
                onClick={onProcess}
                disabled={images.length === 0}
                className="w-full flex items-center justify-center gap-2.5 py-4 px-6 bg-gradient-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400 text-white rounded-2xl font-semibold text-base transition-all shadow-xl shadow-cyan-500/25 hover:shadow-cyan-500/40 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
              >
                <Zap size={20} />
                Process {images.length} Image{images.length !== 1 ? 's' : ''}
              </button>
            </>
          )}
        </div>

        {/* Right Column: Preview */}
        {images.length > 0 && (
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <ImagePreview
                images={images}
                options={{ resize: resizeOptions, watermark: watermarkOptions, rename: renameOptions }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BatchEditor;
